import { downloadFile } from '@fukuri-admins/utils/common';
import { useEffect, useState } from 'react';
import { fetchUserDailyFirstAccessLogCSV } from '~/api/user_daily_first_access_logs';
import { useAuth } from '~/components/auth/AuthProvider';
import {
  ActionTableFilterParamsType,
  defaultActionTableFilterParams,
} from '~/types/table/ActionTableFilterParamsType';
import {
  ActionTableSortParamsType,
  defaultActionTableSortParams,
} from '~/types/table/ActionTableSortParamsType';
import { handleFilter } from '~/utils/filterings';
import { handleSorting } from '~/utils/sortings';
import { convertToPresentationProps } from '../logics/convertToPresentationProps';
import { LoginReportPresentation } from '../presentations';
import { useGetUserDailyFirstAccessLogs } from './useGetUserDailyFirstAccessLogs';

type PresentationProps = React.ComponentProps<typeof LoginReportPresentation>;
type IUseLoginReport = () => {
  state: {
    rows: PresentationProps['rows'];
    currentPage: number;
    totalPage: number;
    totalCount: number;
    isLoading: boolean;
  };
  actions: {
    handlePageChange: PresentationProps['pagerProps']['onPageChange'];
    handleSort: PresentationProps['onSort'];
    handleOutputCsv: PresentationProps['onOutputCsv'];
  };
};

/**
 * LoginReport の状態管理とアクションを提供する
 */
export const useLoginReport: IUseLoginReport = () => {
  const { signOut } = useAuth();

  const [page, setPage] = useState<number>(1);
  const [sortParams, setSortParams] = useState<ActionTableSortParamsType>({
    ...defaultActionTableSortParams,
    column: 'no',
  });
  const [filterParameters, setFilterParameters] = useState<ActionTableFilterParamsType>({
    ...defaultActionTableFilterParams,
    filterColumn: 'user_name',
  });
  const [rows, setRows] = useState<PresentationProps['rows']>([]);

  const { data, headers, isLoading } = useGetUserDailyFirstAccessLogs({ page });

  const totalPage = Number(headers?.['total-pages']) || 0;
  const totalCount = Number(headers?.['total-count']) || 0;

  // データ取得後に rows を変換・フィルタ適用して初期化する
  useEffect(() => {
    if (!data) return;
    const { rows: converted } = convertToPresentationProps({ data });
    handleFilter(converted, filterParameters, false, setRows, setFilterParameters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  /**
   * ページを変更する
   */
  const handlePageChange: ReturnType<IUseLoginReport>['actions']['handlePageChange'] = newPage =>
    setPage(newPage);

  /**
   * カラムヘッダーのクリックでソートする
   */
  const handleSort: ReturnType<IUseLoginReport>['actions']['handleSort'] = column => {
    handleSorting(column, sortParams, rows, setSortParams, setRows);
  };

  /**
   * CSV ファイルをダウンロードする
   */
  const handleOutputCsv: ReturnType<IUseLoginReport>['actions']['handleOutputCsv'] = async () => {
    const { data: csvData } = await fetchUserDailyFirstAccessLogCSV(signOut);
    if (csvData instanceof Blob) {
      downloadFile(csvData, 'user_daily_first_access_log.csv');
    }
  };

  return {
    state: {
      rows,
      currentPage: page,
      totalPage,
      totalCount,
      isLoading,
    },
    actions: {
      handlePageChange,
      handleSort,
      handleOutputCsv,
    },
  };
};
